import { useCallback, useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getStatsAsync } from '@/modules/PetSitter/viewProfileServiceHelper'

import { getProfileAsync } from '@/modules/PetSitter/viewProfileServiceHelper'
import { getReviews } from '@/modules/Reviews/ReviewsServiceHelper'
import { usePetsitterPrice } from '@/hooks/usePetsitterPrice'
import { useNewRequestCartStore } from '@/store/newRequestCartStore'

const REVIEWS_PER_PAGE = 10

type ReviewSummaryItem = {
  comment: string
  reviewDate: string
  displayName: string
  rating: number
  thumbnailUrl: string
} & Record<string, any>

type ReviewsSummaryState = {
  reviews: ReviewSummaryItem[]
  averageRating: number
  reviewsCount: number
}

const getInitialReviewsSummary = () =>
  ({
    reviews: [],
    averageRating: 0,
    reviewsCount: 0,
  } as ReviewsSummaryState)

export const usePetsitterProfileData = ({ customerId, profile }: { customerId: string; profile?: any }) => {
  const cartPayload = useNewRequestCartStore((s) => s.getCartPayload())
  const petsToComplete = useNewRequestCartStore((s) => s.petsToComplete)

  const { isLoading, data: userProfile } = useQuery(
    [`petsitter-profile-${customerId}`],
    () => getProfileAsync(customerId),
    {
      enabled: !!customerId,
      initialData: profile ?? undefined,
      refetchOnWindowFocus: false,
      refetchOnMount: 'always',
      staleTime: 10000,
    },
  )

  const { isLoading: isStatsLoading, data: statsData } = useQuery(
    [`petsitter-stats-${customerId}`],
    () => getStatsAsync(customerId),
    {
      enabled: !!customerId,
      refetchOnWindowFocus: false,
      staleTime: 10000,
    },
  )

  const [reviewsSummary, setReviewsSummary] = useState(getInitialReviewsSummary)
  const [reviewsPage, setReviewsPage] = useState(1)
  const [hasMoreReviews, setHasMoreReviews] = useState(false)
  const [reviewsLoadingMore, setReviewsLoadingMore] = useState(false)

  const mapReviewData = useCallback(
    (review: any) => ({
      ...review,
      comment: review.content,
      reviewDate: review.createdAt,
      displayName: review.author,
      rating: review.rating,
      thumbnailUrl: review.authorAvatarUrl || '',
    }),
    [],
  )

  const fetchReviewsPage = useCallback(
    async (page: number) => {
      if (!userProfile?.uuid) return null

      const reviewsData = await getReviews(userProfile.uuid, {
        page,
        perPage: REVIEWS_PER_PAGE,
        sortBy: 'date',
        direction: 'desc',
      })

      return {
        reviews: reviewsData.lastReviews.map(mapReviewData),
        averageRating: reviewsData.averageRating,
        reviewsCount: reviewsData.totalReviewsCount,
      }
    },
    [mapReviewData, userProfile?.uuid],
  )

  const { isLoading: isInitialReviewsLoading, data: initialReviewsData } = useQuery(
    [`petsitter-reviews-${userProfile?.uuid}`],
    () => fetchReviewsPage(1),
    {
      enabled: !!userProfile?.uuid,
      refetchOnWindowFocus: false,
      staleTime: 10000,
    },
  )

  useEffect(() => {
    if (!userProfile?.uuid) {
      setReviewsSummary(getInitialReviewsSummary())
      setReviewsPage(1)
      setHasMoreReviews(false)
      return
    }
  }, [userProfile?.uuid])

  useEffect(() => {
    if (!initialReviewsData) return

    setReviewsSummary(initialReviewsData)
    setHasMoreReviews(initialReviewsData.reviews.length < initialReviewsData.reviewsCount)
    setReviewsPage(1)
  }, [initialReviewsData])

  const loadMoreReviews = useCallback(async () => {
    if (!hasMoreReviews || reviewsLoadingMore) return

    setReviewsLoadingMore(true)
    try {
      const nextPage = reviewsPage + 1
      const nextPageData = await fetchReviewsPage(nextPage)

      if (!nextPageData) return

      let nextReviewsCount = 0

      setReviewsSummary((prev) => {
        const nextReviews = [...prev.reviews, ...nextPageData.reviews]
        nextReviewsCount = nextReviews.length

        return {
          reviews: nextReviews,
          averageRating: nextPageData.averageRating,
          reviewsCount: nextPageData.reviewsCount,
        }
      })

      setHasMoreReviews(nextReviewsCount < nextPageData.reviewsCount)
      setReviewsPage(nextPage)
    } catch (e) {
      console.error(e)
    } finally {
      setReviewsLoadingMore(false)
    }
  }, [fetchReviewsPage, hasMoreReviews, reviewsLoadingMore, reviewsPage])

  const { petsitterPrice, reduction, quantity } = usePetsitterPrice({
    uuid: userProfile?.uuid,
    cartPayload,
    petsToComplete,
  })

  const profileImages = userProfile?.pictures || []
  const avatarImage = userProfile?.mainPicture

  const allImages = avatarImage
    ? [avatarImage.imageUrl, ...profileImages.map((pic: any) => pic.imageUrl)]
    : profileImages.map((pic: any) => pic.imageUrl)

  return {
    isLoading,
    userProfile,
    reviewsSummary,
    hasMoreReviews,
    reviewsLoadingMore,
    isInitialReviewsLoading,
    loadMoreReviews,
    petsitterPrice,
    reduction,
    quantity,
    allImages,
    statsData,
    isStatsLoading,
  }
}
